import { useQuery } from '@tanstack/react-query';
import { Button, Dropdown, Input, Layout, Typography } from 'antd';
import { DownOutlined, LogoutOutlined, SearchOutlined, SwapOutlined } from '@ant-design/icons';

const SidebarToggleIcon = ({ collapsed }: { collapsed: boolean }) => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'block', transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
  >
    <path d="M10 4l-4 4 4 4" />
  </svg>
);
import { Outlet, useMatch } from 'react-router-dom';
import { useInstitution } from '../contexts/InstitutionContext';
import { ApiError, productApi } from '../api';
import { useAdminSidebar } from './admin/useAdminSidebar';

/**
 * Reads /api/accounts/me via the session cookie set by the accounts app on
 * login. When absent (never logged in, expired cookie) the hat shows a
 * generic placeholder and the dropdown offers "Sign in" instead of Sign out.
 */
function accountsBase(): string {
  return (import.meta.env['VITE_ACCOUNTS_BASE_URL'] as string | undefined) ?? 'http://127.0.0.1:5175';
}

async function signOut(): Promise<void> {
  try {
    await productApi.logout();
  } catch {
    // best-effort; still redirect
  }
  window.location.href = `${accountsBase()}/login`;
}

function goSwitchInstitution(): void {
  // Session stays; /select in the accounts app lets the caller pick another
  // membership. Nothing to clear here.
  window.location.href = `${accountsBase()}/select`;
}

/**
 * Product shell — the top bar that carries: institution switcher (left),
 * global search (centre), user hat (right). Same shape across every product
 * surface (Admin, Teacher, Guardian, Student). The Admin surface adds its
 * own sidebar via AdminShell → see AdminShell.tsx.
 *
 * Session (year + term) lives in the institution switcher subtitle per the
 * Admin.dc.html top-bar pattern.
 */
export function ProductShell() {
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <div style={{ marginLeft: 'var(--velora-sidebar-width, 0px)', transition: 'margin-left 0.2s' }}>
        <TopBar />
        <Layout.Content style={{ background: '#f7f7f5' }}>
          <Outlet />
        </Layout.Content>
      </div>
    </Layout>
  );
}

/**
 * Layout route for product screens that don't nest inside AdminShell (setup,
 * org console, teacher/parent/student surfaces). AdminShell provides its own
 * padded container, so admin pages don't use this. Keeps per-surface pages
 * honest — they render their content, the shell handles the frame.
 */
export function ProductSurface() {
  return (
    <div style={{ padding: 22 }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <Outlet />
      </div>
    </div>
  );
}

function TopBar() {
  const ctx = useInstitution();
  const isAdmin = !!useMatch('/i/:institution/admin/*');
  const sidebar = useAdminSidebar();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => productApi.getMe(),
    retry: false,
    staleTime: 60_000,
  });
  const isAnon = me.isError && me.error instanceof ApiError && me.error.status === 401;

  const membershipHere = me.data?.memberships.find((m) => m.institution.slug === ctx.institution.slug);
  const person = me.data
    ? {
        name: me.data.person.name || me.data.person.email,
        role: membershipHere ? membershipHere.role.replace(/_/g, ' ') : 'no access',
        email: me.data.person.email,
      }
    : { name: 'Not signed in', role: '—', email: null };

  return (
    <div
      style={{
        height: 50,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '0 12px',
        background: '#ffffff',
        borderBottom: '1px solid #e6e6e9',
        flexShrink: 0,
      }}
    >
      {isAdmin && (
        <Button
          type="text"
          size="small"
          icon={<SidebarToggleIcon collapsed={sidebar.collapsed} />}
          onClick={sidebar.toggle}
          aria-label={sidebar.collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        />
      )}
      {/* Institution switcher */}
      <button
        type="button"
        style={{
          appearance: 'none',
          border: '1px solid #e6e6e9',
          background: '#fff',
          padding: '4px 10px',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          height: 32,
        }}
        title="Institution switcher — placeholder for now"
      >
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            background: '#14213d',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 10,
          }}
        >
          {ctx.institution.name.charAt(0).toUpperCase()}
        </div>
        <div style={{ textAlign: 'left', lineHeight: 1.1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#14213d' }}>{ctx.institution.name}</div>
          <div style={{ fontSize: 10.5, color: '#5b6478' }}>
            {ctx.session.label}
            {ctx.session.term ? ` · ${ctx.session.term}` : ''}
          </div>
        </div>
        <DownOutlined style={{ fontSize: 10, color: '#8a94a6' }} />
      </button>

      {/* Global search */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
        <Input
          size="small"
          placeholder="Search students, staff, invoices…"
          prefix={<SearchOutlined style={{ color: '#8a94a6' }} />}
          suffix={
            <span
              style={{
                fontSize: 10,
                color: '#8a94a6',
                border: '1px solid #e6e6e9',
                padding: '1px 5px',
                borderRadius: 4,
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              }}
            >
              ⌘K
            </span>
          }
          style={{ maxWidth: 400 }}
          readOnly
          title="Global search ships later"
        />
      </div>

      {/* Person hat — dropdown with sign-out */}
      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            { key: 'signed-in', label: person.email ?? 'Not signed in', disabled: true },
            { type: 'divider' as const },
            ...(!isAnon && (me.data?.memberships.length ?? 0) > 1
              ? [{
                  key: 'switch',
                  icon: <SwapOutlined />,
                  label: 'Switch institution',
                  onClick: () => goSwitchInstitution(),
                }]
              : []),
            {
              key: 'signout',
              icon: <LogoutOutlined />,
              label: isAnon ? 'Sign in' : 'Sign out',
              onClick: () => void signOut(),
            },
          ],
        }}
      >
        <button
          type="button"
          style={{
            appearance: 'none',
            border: '1px solid transparent',
            background: 'transparent',
            padding: '2px 4px',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div style={{ textAlign: 'right', lineHeight: 1.15 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#14213d' }}>{person.name}</div>
            <div style={{ fontSize: 11, color: '#5b6478' }}>{person.role}</div>
          </div>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#2f6ab0',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {person.name
              .split(/\s+/)
              .map((p) => p[0])
              .filter(Boolean)
              .slice(0, 2)
              .join('')
              .toUpperCase()}
          </div>
        </button>
      </Dropdown>
    </div>
  );
}

// unused import guard for tree-shakers that don't infer JSX use
export const _typo = Typography;
